import { makeAutoObservable, observable, runInAction } from "mobx";
import { track, type AnalyticsSource } from "@/lib/analytics";

import type { RootStore } from "./root.store";
import type {
  IWorkspaceTemplate,
  IWorkspaceTemplateCreateInput,
  IWorkspaceTemplateVersion,
} from "@/types/workspace-template";

/**
 * Server-data store for workspace templates (list / create / delete), keyed by
 * workspace slug. Mirrors the {@link WorkspaceCustomFieldsStore} shape: a real
 * MobX store over a mocked-in-tests service, exposing loading / mutating / error
 * state for the management UI. There is no client-persisted UI preference here,
 * so — unlike the list-preferences stores — it does not touch localStorage.
 *
 * List order is the server's: `my_last_applied_at DESC NULLS LAST, created_at
 * DESC` — the requesting user's most-recently-applied template leads. The store
 * preserves that order verbatim (it does NOT re-sort), and optimistic mutations
 * keep it: a create prepends (a brand-new template is the freshest action and is
 * reconciled to its true slot on the next fetch, and a re-capture of an existing
 * name replaces its row rather than duplicating it), a set-default patches the
 * row in place (default is not a sort key), and a delete filters in place.
 */
export class WorkspaceTemplatesStore {
  templatesBySlug = observable.map<string, IWorkspaceTemplate[]>();
  /** Version-history cache, keyed by template id (newest-first). */
  versionsByTemplateId = observable.map<string, IWorkspaceTemplateVersion[]>();
  isLoading = false;
  isMutating = false;
  /** Id of the template a delete is in flight for (drives per-row spinners). */
  deletingId: string | null = null;
  /** Id of the template a set-default is in flight for. */
  settingDefaultId: string | null = null;
  /** Last list-load failure, surfaced to the UI. Cleared when a load starts. */
  loadError: Error | null = null;

  private rootStore: RootStore;
  // In-flight dedup for concurrent ensure() calls on the same slug. Not observable.
  private inflightList = new Map<string, Promise<IWorkspaceTemplate[]>>();
  // In-flight dedup for concurrent ensureVersions() calls, keyed by template id.
  private inflightVersions = new Map<string, Promise<IWorkspaceTemplateVersion[]>>();

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(
      this,
      { rootStore: false, inflightList: false, inflightVersions: false } as never,
      { autoBind: true },
    );
  }

  private currentSlug(): string | null {
    return this.rootStore.router.workspaceSlug ?? null;
  }

  /** Templates for the current workspace (default-first). */
  get templates(): IWorkspaceTemplate[] {
    const slug = this.currentSlug();
    if (!slug) return [];
    return this.templatesBySlug.get(slug) ?? [];
  }

  /** Fetch the usable-templates list for a workspace, replacing the cache. */
  async fetch(slug: string): Promise<IWorkspaceTemplate[]> {
    runInAction(() => {
      this.isLoading = true;
      this.loadError = null;
    });
    try {
      const templates = await this.rootStore.workspaceTemplateService.list(slug);
      runInAction(() => {
        // Preserve the server order (my_last_applied_at DESC NULLS LAST,
        // created_at DESC) — don't re-sort against it.
        this.templatesBySlug.set(slug, templates);
      });
      return templates;
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err));
      console.error(`Failed to load workspace templates for "${slug}"`, err);
      runInAction(() => {
        this.loadError = error;
      });
      throw error;
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  }

  /** Load the list for a workspace once; concurrent calls share one request. */
  async ensure(slug: string): Promise<IWorkspaceTemplate[]> {
    const cached = this.templatesBySlug.get(slug);
    if (cached) return cached;
    const inflight = this.inflightList.get(slug);
    if (inflight) return inflight;
    const p = this.fetch(slug).finally(() => {
      this.inflightList.delete(slug);
    });
    this.inflightList.set(slug, p);
    return p;
  }

  /** Capture the CURRENT workspace into a new named template and cache it. */
  async create(
    slug: string,
    input: IWorkspaceTemplateCreateInput,
    source: AnalyticsSource = "page",
  ): Promise<IWorkspaceTemplate> {
    runInAction(() => {
      this.isMutating = true;
    });
    try {
      const created = await this.rootStore.workspaceTemplateService.create(slug, input);
      runInAction(() => {
        // Prepend — a just-created template is the freshest action; the next
        // fetch reconciles it to its true server-ordered slot. Capture is
        // idempotent per (creator, name), so re-capturing an existing name
        // returns that SAME template id updated in place (version bumped):
        // drop the stale row by id rather than prepending a duplicate of it.
        const current = this.templatesBySlug.get(slug) ?? [];
        this.templatesBySlug.set(slug, [created, ...current.filter((t) => t.id !== created.id)]);
      });
      track("template_created", {
        scope: "workspace",
        template_id: created.id,
        capture_scope: input.scope,
        source,
      });
      return created;
    } catch (err) {
      console.error(`Failed to create workspace template in "${slug}"`, { input, err });
      throw err;
    } finally {
      runInAction(() => {
        this.isMutating = false;
      });
    }
  }

  /** Delete a template and drop it from the cache. */
  async remove(slug: string, templateId: string, source: AnalyticsSource = "page"): Promise<void> {
    runInAction(() => {
      this.deletingId = templateId;
    });
    try {
      await this.rootStore.workspaceTemplateService.remove(slug, templateId);
      runInAction(() => {
        const current = this.templatesBySlug.get(slug);
        if (current) {
          this.templatesBySlug.set(
            slug,
            current.filter((t) => t.id !== templateId),
          );
        }
      });
      track("template_deleted", { scope: "workspace", template_id: templateId, source });
    } catch (err) {
      console.error(`Failed to delete workspace template "${templateId}" in "${slug}"`, err);
      throw err;
    } finally {
      runInAction(() => {
        this.deletingId = null;
      });
    }
  }

  /** Cached versions for a template (newest-first), or undefined if not loaded. */
  versionsFor(templateId: string): IWorkspaceTemplateVersion[] | undefined {
    return this.versionsByTemplateId.get(templateId);
  }

  /** Fetch a template's version history, replacing the per-template cache. Read
   *  by the reset dialog's optional "reset from a past version" picker. */
  async fetchVersions(slug: string, templateId: string): Promise<IWorkspaceTemplateVersion[]> {
    try {
      const versions = await this.rootStore.workspaceTemplateService.listVersions(slug, templateId);
      runInAction(() => {
        this.versionsByTemplateId.set(templateId, versions);
      });
      return versions;
    } catch (err) {
      console.error(
        `Failed to load versions for workspace template "${templateId}" in "${slug}"`,
        err,
      );
      throw err;
    }
  }

  /** Load a template's versions once; concurrent calls share one request. */
  async ensureVersions(slug: string, templateId: string): Promise<IWorkspaceTemplateVersion[]> {
    const cached = this.versionsByTemplateId.get(templateId);
    if (cached) return cached;
    const inflight = this.inflightVersions.get(templateId);
    if (inflight) return inflight;
    const p = this.fetchVersions(slug, templateId).finally(() => {
      this.inflightVersions.delete(templateId);
    });
    this.inflightVersions.set(templateId, p);
    return p;
  }

  /**
   * Mark a template as the default new workspaces are seeded from. The backend
   * enforces a single default, so on success we patch the returned template into
   * the cache AND clear `is_default` on every other cached template to mirror
   * that invariant locally. Default is not a sort key, so the row is patched in
   * place — the list keeps its server (applied-time) order.
   */
  async setDefault(
    slug: string,
    templateId: string,
    source: AnalyticsSource = "page",
  ): Promise<IWorkspaceTemplate> {
    runInAction(() => {
      this.settingDefaultId = templateId;
    });
    try {
      const updated = await this.rootStore.workspaceTemplateService.setDefault(slug, templateId);
      runInAction(() => {
        const current = this.templatesBySlug.get(slug);
        if (current) {
          this.templatesBySlug.set(
            slug,
            current.map((t) =>
              t.id === templateId ? updated : t.is_default ? { ...t, is_default: false } : t,
            ),
          );
        }
      });
      track("template_updated", {
        scope: "workspace",
        template_id: templateId,
        fields: ["default"],
        source,
      });
      return updated;
    } catch (err) {
      console.error(
        `Failed to set workspace template "${templateId}" as default in "${slug}"`,
        err,
      );
      throw err;
    } finally {
      runInAction(() => {
        this.settingDefaultId = null;
      });
    }
  }
}
